import { IFormField, IFormatableField, IText, logger } from "cayo.ui";
import { Entity } from "@cayo/shared/api/types/cayo-graph";
import { Formatters } from "../../api/schema.api";
import { objectUtils } from "@cayo/shared/utils/object";
import { TabEx } from "../Navigation/Pivot";
import { formComponentTypes } from "./renderers/form-renderers";

type odataTypes = "#Edm.DateTimeOffset" | "#Edm.Duration";

const isOdataType = (value: unknown): value is odataTypes => {
  return value === "#Edm.DateTimeOffset" || value === "#Edm.Duration";
};

const odataTypeToComponentTypeMap: { [key: string]: formComponentTypes } = {
  "#Int32": "number",
  "#Collection(String)": "mv.text",
  "#Edm.Boolean": "checkbox",
  "#Edm.Enum": "dropdown",
  "cayo.graph.customSchedule": "inplaceschedule",
  "#Collection(cayo.graph.softLink)": "objectlinks",
  "#cayo.graph.softLink": "objectlink",
};

type FormFieldProps = keyof Pick<
  IFormField,
  "label" | "required" | "disabled" | "order" | "placeHolder" | "dataType" | "tooltip" | "readOnly"
>;

const odataTypeFormatters: Record<odataTypes, Formatters> = {
  "#Edm.DateTimeOffset": "dateTime",
  "#Edm.Duration": "duration",
};

const componentPropNames: Record<FormFieldProps | "pattern", string> = {
  label: "cayo.schema.displayName",
  required: "cayo.schema.required",
  disabled: "cayo.schema.disabled",
  readOnly: "cayo.schema.readOnly",
  order: "cayo.schema.propertiesFormPosition",
  pattern: "cayo.schema.pattern",
  placeHolder: "cayo.schema.placeholder",
  tooltip: "cayo.schema.toolTip",
  dataType: "odata.type",
};

const getOdataProp = (propName: string, suffix: string) => {
  return `${propName}@${suffix}`;
};

const getOdataType = (object: Entity, propName: string) => {
  const typeProp = getOdataProp(propName, "odata.type");
  const result = object[typeProp];
  return result;
};

const toTabs = (object: Entity) => {
  const result: TabEx[] = [];
  const hasSchedule = object["schedule@odata.type"] === "cayo.graph.customSchedule";
  const parameters =
    object["schedule@cayo.schema.parameters"] ?? "oneTime|daily|weekly|monthly|continuous|custom";
  if (hasSchedule) {
    result.push({
      headerText: "Schedule",
      customView: `schedule`,
      parameters,
      schemePath: object["objectPath"] + "/schedule",
      isDataset: false,
      order: object["schedule@cayo.schema.navigationPropertyPosition"] ?? Number.MAX_VALUE,
    });
  }
  return result;
};

const toUIAnnotations = (object: Entity, disabled?: boolean) => {
  const odataKeys = Object.keys(object).filter((k) => k.indexOf("@") > 0);
  const groupedOdataProps = objectUtils.groupBy(odataKeys, (k) => k.split("@")[0]);
  const result: IFormField[] = [];
  for (const group of groupedOdataProps) {
    const propName = group[0];
    const otype = getOdataType(object, propName);
    const uiType = odataTypeToComponentTypeMap[otype] ?? "text";

    if (!uiType) {
      continue;
    }

    const uiComp: IFormField = { type: uiType, name: propName };
    // const oDataPropValue = object[propName];
    // if (oDataPropValue && uiType !== "mv.text") {
    //   (uiComp as unknown as any).value = oDataPropValue;
    // } else if (disabled && uiType === "mv.text") {
    //   (uiComp as unknown as any).value = mvConverters.to[uiType as string](oDataPropValue);
    // }

    if (propName === "pwdLastSetDT") {
      logger.debug("ddd");
    }

    Object.entries(componentPropNames).forEach((e) => {
      const [k, v] = e;

      const odataPropName = getOdataProp(propName, v);
      let propValue = object[odataPropName!];

      if (!propValue) {
        const odataPropNameLc = getOdataProp(propName, v.toLowerCase());
        if (odataPropNameLc !== odataPropName) {
          propValue = object[odataPropNameLc!];
        }
      }

      if (propValue) {
        // @ts-expect-error TS7053 TODO: Refactor object index types.
        uiComp[k] = propValue;
      }
    });

    const rawReadOnly = object[getOdataProp(propName, "cayo.schema.readOnly")];
    if (rawReadOnly === false && uiComp.type === "text") {
      uiComp.type = "textfield";
    }

    if (uiComp.label) {
      if (uiComp.type === "text" && !(uiComp as IText).nullValueDecorator) {
        (uiComp as IText).nullValueDecorator = "[Not set]"; // TODO: use intl
      }

      if (disabled) {
        uiComp.readOnly = disabled;
      }

      const formattedField = uiComp as IFormatableField;
      const formatter =
        formattedField.formatter ??
        (isOdataType(uiComp.dataType) ? odataTypeFormatters[uiComp.dataType] : undefined);

      if (formatter) {
        (uiComp as IFormatableField).formatter = formatter;
      }

      result.push(uiComp);
    }
  }

  result.sort((a, b) => (a.order ?? Number.MAX_VALUE) - (b.order ?? Number.MAX_VALUE));

  return result;
};

const odataAnnotationsUtils = {
  getOdataProp,
  toUIAnnotations,
  toTabs,
};

export default odataAnnotationsUtils;
